import apiClient from "../lib/api-client";
import type {
  EffectiveAccessContext,
  PageOverrideMode,
} from "@medicorepay/shared";
import type { LoginFormData } from "../lib/validations/auth.schema";
import type { TenantSidebarConfig } from "../stores/authStore";

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role?: string;
    roleName?: string | null;
    roleId?: string | null;
    permissions?: string[];
    permVersion?: number;
    accessVersion?: number;
    tenantId: string;
    storeId?: string;
    stores?: Array<{ id: string; name: string }>;
    phone?: string;
    enabledModules?: string[];
    pageOverrides?: Partial<Record<string, PageOverrideMode>>;
    disabledPageKeys?: string[];
    accessContext?: EffectiveAccessContext;
    sidebarConfig?: TenantSidebarConfig;
  };
  message?: string;
}

export type ActiveStoreAuthResponse = Omit<AuthResponse, "refreshToken"> & {
  refreshToken: string | null;
};

const authMutationConfig = () => ({
  headers: { "Idempotency-Key": crypto.randomUUID() },
});

export const authService = {
  async login(credentials: LoginFormData): Promise<AuthResponse> {
    const response = await apiClient.post(
      "/auth/login",
      credentials,
      authMutationConfig(),
    );
    return response.data;
  },

  async logout(accessToken?: string | null): Promise<void> {
    await apiClient.post("/auth/logout", undefined, {
      headers: {
        "Idempotency-Key": crypto.randomUUID(),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
  },

  async selectActiveStore(storeId: string): Promise<ActiveStoreAuthResponse> {
    const response = await apiClient.post(
      "/auth/active-store",
      { storeId },
      authMutationConfig(),
    );
    return response.data;
  },

  recoverPendingSession(): Promise<boolean> {
    return apiClient.recoverPendingSession();
  },

  runSessionMutation<T>(operation: () => Promise<T>): Promise<T> {
    return apiClient.runSessionMutation(operation);
  },

  async getProfile(): Promise<{ user: AuthResponse["user"] }> {
    const response = await apiClient.post("/auth/me");
    return response.data;
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await apiClient.post(
      "/auth/refresh",
      { refreshToken },
      authMutationConfig(),
    );
    return response.data;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await apiClient.post(
      "/auth/forgot-password",
      { email },
      authMutationConfig(),
    );
    return response.data;
  },

  async resetPassword(
    token: string,
    password: string,
  ): Promise<{ message: string }> {
    const response = await apiClient.post(
      "/auth/reset-password",
      { token, password },
      authMutationConfig(),
    );
    return response.data;
  },
};
